


import React, { useEffect, useState } from "react";
import { saveBooking, getArtistBookings } from "../../services/bookingService";
import api from "../../api/axiosClient";
import { useNavigate } from "react-router-dom";
import { 
  calculateBookingTotal, 
  calculateBookingItemTotal,
  getLatestServicePrice as getServicePrice 
} from "../../utils/bookingCalculations";
// Removed getUsers import; client fields will be editable

const AddBooking = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState<any[]>([]);
  const [servicePrices, setServicePrices] = useState<any[]>([]);
  const [outsourcePersons, setOutsourcePersons] = useState<any[]>([]);
  const [bookingSlots, setBookingSlots] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<any>({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    clientAddress: '',
    clientNotes: '',
    eventDate: '',
    eventTime: '',
    venue: '',
    bookingAmount: '',
    status: 'Enquired',
    bookingItems: [],
  });
  const [allBookings, setAllBookings] = useState<any[]>([]);

  useEffect(() => {
    api.get('/Service').then(res => setServices(res.data));
    api.get('/ServicePrice/artist').then(res => setServicePrices(res.data));
    api.get('/OutsourcePerson/artist').then(res => setOutsourcePersons(Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.items) ? res.data.items : [])));
    api.get('/BookingSlot').then(res => setBookingSlots(res.data)).catch(() => setBookingSlots([]));
    getArtistBookings().then(setAllBookings).catch(() => setAllBookings([]));
  }, []);

  const getLatestServicePrice = (serviceId: number) => {
    return getServicePrice(servicePrices, serviceId);
  };

  const computeBookingTotal = () => {
    return calculateBookingTotal(form.bookingItems);
  };

  const handleAddItem = () => {
    setForm((f: any) => ({
      ...f,
      bookingItems: [
        ...f.bookingItems,
        {
          id: 0,
          isDeleted: false,
          serviceId: '',
          service: null,
          outsourcePersonIds: [], // Array of outsource person IDs
          outsourcePersons: [], // Array of outsource person objects
          description: '',
          serviceCount: 1,
          amount: 0,
          bookingDate: form.eventDate || '',
          bookingSlotId: null,
        },
      ],
    }));
  };

  const handleDeleteItem = (idx: number) => {
    setForm((f: any) => {
      const items = [...f.bookingItems];
      if (items[idx].id && items[idx].id > 0) {
        items[idx] = { ...items[idx], isDeleted: true };
      } else {
        items.splice(idx, 1);
      }
      return { ...f, bookingItems: items };
    });
  };

  // Block slots already booked for the same date by another client
  const getBookedSlotsForDate = (dateStr: string) => {
    const result = new Set<number>();
    if (!dateStr) return result;
    for (const b of allBookings) {
      // Only consider bookings for the same event date
      const items = Array.isArray(b?.bookingItems) ? b.bookingItems : [];
      for (const it of items) {
        if (it?.isDeleted) continue;
        const d = it?.bookingDate ? (it.bookingDate as string).slice(0, 10) : '';
        const sid = Number(it?.bookingSlotId);
        if (!sid || !d) continue;
        if (d === dateStr) {
          result.add(sid);
        }
      }
    }
    return result;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Slot selection validation: every booking item must have a slot selected
      const missingSlotIdx = form.bookingItems.findIndex((it: any) => !it.isDeleted && (!it.bookingSlotId || it.bookingSlotId === ''));
      if (missingSlotIdx !== -1) {
        setLoading(false);
        alert('Please select a slot for every booking item.');
        return;
      }
      const calcTotal = computeBookingTotal();
      const artistId = (typeof window !== 'undefined') ? Number(localStorage.getItem('makeupArtistId') || 0) : 0;
      const payload = {
        MakeupArtistId: artistId,
        EventDate: form.eventDate,
        Venue: form.venue,
        Status: form.status,
        BookingAmount:
          form.bookingAmount === '' || form.bookingAmount === undefined || form.bookingAmount === null
            ? calcTotal
            : Number(form.bookingAmount),
        Client: {
          FullName: form.clientName,
          Phone: form.clientPhone,
          Email: form.clientEmail,
          Address: form.clientAddress,
          Notes: form.clientNotes,
        },
        BookingItems: form.bookingItems.filter((it: any) => !it.isDeleted).map((it: any) => ({
          ServiceId: it.service?.id || it.serviceId || 0,
          OutsourcePersonIds: it.outsourcePersonIds || [], // Send array of IDs
          Description: it.description || '',
          serviceCount: it.serviceCount || 1,
          amount: Number(it.amount) || 0,
          BookingDate: it.bookingDate || null,
          BookingSlotId: it.bookingSlotId || null,
        })),
      };
      console.log('Payload before prepareBookingPayload:', payload);
      await saveBooking(payload);
      navigate('/artist/bookings');
    } catch (err) {
      console.error('Error saving booking:', err);
      alert('Failed to save booking.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto bg-background rounded-2xl shadow-lg">
      <h2 className="text-3xl font-semibold mb-6 text-secondary font-sans tracking-tight">Add Booking</h2>
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <div className="flex flex-col gap-1">
            <label className="font-semibold text-secondary">Client Name:</label>
            <input
              type="text"
              value={form.clientName}
              onChange={e => setForm({ ...form, clientName: e.target.value })}
              className="border border-secondary/30 rounded-xl px-3 py-2 w-full bg-white focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
              required
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold text-secondary">Phone Number:</label>
            <input
              type="text"
              value={form.clientPhone}
              onChange={e => setForm({ ...form, clientPhone: e.target.value })}
              className="border border-secondary/30 rounded-xl px-3 py-2 w-full bg-white focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
              placeholder="Optional"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold text-secondary">Email:</label>
            <input
              type="email"
              value={form.clientEmail}
              onChange={e => setForm({ ...form, clientEmail: e.target.value })}
              className="border border-secondary/30 rounded-xl px-3 py-2 w-full bg-white focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
              placeholder="Optional"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold text-secondary">Address:</label>
            <input
              type="text"
              value={form.clientAddress}
              onChange={e => setForm({ ...form, clientAddress: e.target.value })}
              className="border border-secondary/30 rounded-xl px-3 py-2 w-full bg-white focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
              placeholder="Optional"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold text-secondary">Notes:</label>
            <input
              type="text"
              value={form.clientNotes}
              onChange={e => setForm({ ...form, clientNotes: e.target.value })}
              className="border border-secondary/30 rounded-xl px-3 py-2 w-full bg-white focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
              placeholder="Optional"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold text-secondary">Event Date:</label>
            <input
              type="date"
              value={form.eventDate}
              onChange={e => setForm({ ...form, eventDate: e.target.value })}
              className="border border-secondary/30 rounded-xl px-3 py-2 w-full bg-white focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
              required
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold text-secondary">Venue:</label>
            <input
              type="text"
              value={form.venue}
              onChange={e => setForm({ ...form, venue: e.target.value })}
              className="border border-secondary/30 rounded-xl px-3 py-2 w-full bg-white focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
              required
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold text-secondary">Total Amount:</label>
            <span className="border border-secondary/30 rounded-xl px-3 py-2 bg-background">₹ {computeBookingTotal()}</span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold text-secondary">Booking Amount:</label>
            <input
              type="number"
              value={form.bookingAmount}
              onChange={e => setForm({ ...form, bookingAmount: e.target.value })}
              className="border border-secondary/30 rounded-xl px-3 py-2 w-full bg-white focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold text-secondary">Discount Given:</label>
            <span className="border border-secondary/30 rounded-xl px-3 py-2 bg-background">₹ {Math.max(0, computeBookingTotal() - (Number(form.bookingAmount) || 0)).toFixed(2)}</span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold text-secondary">Status:</label>
            <select
              value={form.status}
              onChange={e => setForm({ ...form, status: e.target.value })}
              className="border border-secondary/30 rounded-xl px-3 py-2 w-full bg-white focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
            >
              <option value="booked">Booked</option>
              <option value="confirmed">Confirmed</option>
              <option value="enquired">Enquired</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>
        </div>

        {/* Booking Items */}
        <div>
          <div className="mb-4 flex justify-between items-center">
            <strong className="text-lg text-secondary font-sans">Booking Items</strong>
            <button
              type="button"
              className="btn-primary"
              onClick={handleAddItem}
            >+ Add Item</button>
          </div>
          <div className="space-y-6">
            {form.bookingItems.filter((it: any) => !it.isDeleted).map((item: any, idx: number) => (
              <div key={`item-block-${idx}`} className="card border border-secondary/20 rounded-2xl bg-background relative">
                <button
                  type="button"
                  className="absolute top-2 right-2 p-1"
                  title="Delete"
                  onClick={() => handleDeleteItem(idx)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6 text-red-600">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5v9.75A2.25 2.25 0 009 19.5h6a2.25 2.25 0 002.25-2.25V7.5m-10.5 0h10.5m-10.5 0V6.75A2.25 2.25 0 018.25 4.5h7.5a2.25 2.25 0 012.25 2.25V7.5m-10.5 0h10.5M9.75 10.5v4.5m4.5-4.5v4.5" />
                  </svg>
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  {/* Service */}
                  <div className="flex flex-col gap-1">
                    <label className="font-medium">Service</label>
                    <select
                      value={(item.service?.id ?? item.serviceId) || ''}
                      onChange={e => {
                        const serviceId = Number(e.target.value);
                        const selectedService = services.find((s: any) => s.id === serviceId);
                        const price = getLatestServicePrice(serviceId);
                        const updated = [...form.bookingItems];
                        updated[idx] = {
                          ...item,
                          service: selectedService,
                          serviceId,
                          amount: price,
                        };
                        setForm({ ...form, bookingItems: updated });
                      }}
                      className="border rounded px-2 py-1 w-full"
                    >
                      <option value="">Select Service</option>
                      {services.map((s: any) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  {/* Service Count */}
                  <div className="flex flex-col gap-1">
                    <label className="font-medium">Number Of Makeup</label>
                    <input
                      type="number"
                      min="1"
                      value={item.serviceCount || 1}
                      onChange={e => {
                        const count = Math.max(1, Number(e.target.value) || 1);
                        const serviceId = item.service?.id || item.serviceId;
                        const price = serviceId ? getLatestServicePrice(serviceId) : (item.amount || 0);
                        const updated = [...form.bookingItems];
                        updated[idx] = {
                          ...item,
                          serviceCount: count,
                          amount: price, // Update price from service price list
                        };
                        setForm({ ...form, bookingItems: updated });
                      }}
                      className="border rounded px-2 py-1 w-full"
                    />
                  </div>
                  {/* Comment */}
                  <div className="flex flex-col gap-1">
                    <label className="font-medium">Comment</label>
                    <input
                      type="text"
                      value={item.description || ''}
                      onChange={e => {
                        const updated = [...form.bookingItems];
                        updated[idx] = { ...item, description: e.target.value };
                        setForm({ ...form, bookingItems: updated });
                      }}
                      className="border rounded px-2 py-1 w-full"
                    />
                  </div>
                  {/* Total Amount */}
                  <div className="flex flex-col gap-1">
                    <label className="font-medium">Total Amount (Auto-calculated)</label>
                    <input
                      type="number"
                      value={calculateBookingItemTotal(item.serviceCount || 1, Number(item.amount) || 0)}
                      readOnly
                      className="border rounded px-2 py-1 w-full bg-gray-100"
                    />
                  </div>
                  {/* Item Date */}
                  <div className="flex flex-col gap-1">
                    <label className="font-medium">Item Date</label>
                    <input
                      type="date"
                      value={(item.bookingDate || form.eventDate || '').slice(0,10)}
                      onChange={e => {
                        const updated = [...form.bookingItems];
                        updated[idx] = { ...item, bookingDate: e.target.value };
                        setForm({ ...form, bookingItems: updated });
                      }}
                      className="border rounded px-2 py-1 w-full"
                    />
                  </div>
                  {/* Slot */}
                  <div className="flex flex-col gap-1">
                    <label className="font-medium">Slot</label>
                    {(() => {
                      const dateKey = (item.bookingDate || form.eventDate || '').slice(0, 10);
                      const blocked = getBookedSlotsForDate(dateKey);
                      const options = bookingSlots.filter((s: any) => !blocked.has(s.id));
                      const selectedBlocked = item.bookingSlotId && blocked.has(Number(item.bookingSlotId));
                      return (
                        <div>
                          <select
                            value={item.bookingSlotId || ''}
                            onChange={e => {
                              const updated = [...form.bookingItems];
                              updated[idx] = { ...item, bookingSlotId: e.target.value ? Number(e.target.value) : null };
                              setForm({ ...form, bookingItems: updated });
                            }}
                            className="border rounded px-2 py-1 w-full"
                          >
                            <option value="">Select Slot</option>
                            {options.map((s: any) => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                          {selectedBlocked && (
                            <div className="text-xs text-red-600 mt-1">This slot is already booked for {dateKey} by another client.</div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  {/* Outsource Persons (Multiple Selection) */}
                  <div className="flex flex-col gap-1 md:col-span-2">
                    <label className="font-medium">Outsource Persons</label>
                    <div className="border rounded px-3 py-2 w-full bg-white min-h-[42px] flex flex-wrap gap-2">
                      {(item.outsourcePersonIds || []).map((personId: number) => {
                        const person = outsourcePersons.find((p: any) => p.id === personId);
                        return person ? (
                          <span key={personId} className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-lg text-sm">
                            {person.name}
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...form.bookingItems];
                                updated[idx] = {
                                  ...item,
                                  outsourcePersonIds: item.outsourcePersonIds.filter((id: number) => id !== personId),
                                  outsourcePersons: (item.outsourcePersons || []).filter((p: any) => p.id !== personId)
                                };
                                setForm({ ...form, bookingItems: updated });
                              }}
                              className="text-primary hover:text-red-600 ml-1"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                    <select
                      onChange={e => {
                        const personId = Number(e.target.value);
                        if (!personId) return;
                        const selected = outsourcePersons.find((p: any) => p.id === personId);
                        if (!selected) return;
                        const currentIds = item.outsourcePersonIds || [];
                        if (currentIds.includes(personId)) return; // Already added
                        const updated = [...form.bookingItems];
                        updated[idx] = {
                          ...item,
                          outsourcePersonIds: [...currentIds, personId],
                          outsourcePersons: [...(item.outsourcePersons || []), selected]
                        };
                        setForm({ ...form, bookingItems: updated });
                        e.target.value = ''; // Reset dropdown
                      }}
                      value=""
                      className="border rounded px-2 py-1 w-full mt-2"
                    >
                      <option value="">+ Add Outsource Person</option>
                      {outsourcePersons
                        .filter((p: any) => !(item.outsourcePersonIds || []).includes(p.id))
                        .map((p: any) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            type="submit"
            disabled={loading}
            className="btn-primary px-4 py-2"
          >
            {loading ? "Saving..." : "Save Booking"}
          </button>
          <button
            type="button"
            className="bg-gray-300 px-4 py-2 rounded"
            onClick={() => navigate("/artist/bookings")}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBooking;
